<template>
	<div class="m-w-100">
		<!-- Overflow + badge: inline layout with badge overlapping last chip -->
		<div
			v-if="tags.length > 0 && showBadge && remainingCount > 0"
			class="d-flex align-center ga-1"
			:class="{ 'flex-nowrap': maxVisible === 1, 'flex-wrap': maxVisible > 1 }"
			@mouseenter="onActivatorEnter"
			@mouseleave="onActivatorLeave">
			<template v-for="(tag, index) in visibleTags" :key="tag.id || tag.name">
				<!-- LSP-19649: v-on the listener only when the dialog can actually open. Binding @click
				     unconditionally is what made these chips paint a pointer, take a tab stop and ripple on
				     every desktop pointer, where maybeOpenDialog is a no-op. -->
				<Chip
					v-if="index < visibleTags.length - 1"
					:label="tag"
					class="flex-shrink-0"
					:small="small"
					v-on="overflowChipListeners" />
				<div v-else class="d-flex align-center flex-nowrap ga-1 m-w-100">
					<Chip
						:label="tag"
						class="flex-shrink-1 overflow-hidden"
						:small="small"
						style="min-width: 0"
						v-on="overflowChipListeners" />
					<v-tooltip
						v-model="tooltipActive"
						:location="bottomTooltip ? 'bottom' : 'top'"
						:offset="20"
						content-class="custom-tooltip"
						:disabled="display.mobile.value || isTouch"
						:open-on-hover="false"
						:open-on-click="false">
						<template #activator="{ props }">
							<div
								class="cursor-pointer flex-shrink-0"
								style="position: relative"
								v-bind="props"
								role="button"
								tabindex="0"
								@click.stop="maybeOpenDialog"
								@keyup.enter.stop="maybeOpenDialog">
								<v-badge
									color="primary"
									overlap
									class="cursor-pointer"
									offset-y="-6"
									:content="`+${remainingCount}`"
									style="margin-left: 6px">
									<span class="badge-anchor"></span>
								</v-badge>
							</div>
						</template>
						<v-card
							rounded="xl"
							class="border tag-tooltip-card"
							v-show="!(display.mobile.value || isTouch)"
							:elevation="0"
							@mouseenter="onTooltipEnter"
							@mouseleave="onTooltipLeave"
							@mousescroll.stop>
							<v-card-text class="d-flex ga-1 m-w-100 pa-2 tag-tooltip-content flex-wrap">
								<Chip v-for="tag in tags" :key="tag.id || tag.name" :label="tag" :small="small" />
							</v-card-text>
						</v-card>
					</v-tooltip>
				</div>
			</template>
		</div>

		<!-- Show tags when badge is enabled but there is no overflow -->
		<div v-else-if="tags.length > 0 && showBadge && remainingCount === 0" class="m-w-100 text-ellipsis">
			<div class="d-flex ga-1 align-center flex-wrap">
				<Chip v-for="tag in visibleTags" :key="tag.id || tag.name" :label="tag" class="flex-shrink-0" :small="small" />
			</div>
		</div>
		<!-- Showing the tags in a single line is useful for layouts where we have lots of space.-->
		<div v-else-if="tags.length > 0 && !showBadge" class="m-w-100 text-ellipsis">
			<div class="d-flex ga-1 flex-wrap">
				<template v-for="tag in tags" :key="tag.id || tag.name">
					<Chip :small="small" :label="tag" :closable="closable" @click:close="$emit('click:close', tag)" />
				</template>
			</div>
		</div>
		<!-- Dialog used for overflow on click -->
		<v-dialog v-model="dialogVisible" max-width="480">
			<v-card rounded="xl" class="w-100">
				<v-card-title class="py-2 px-3">Tags</v-card-title>
				<v-card-text class="pa-2">
					<div class="d-flex ga-1 tag-tooltip-content tag-dialog-content flex-wrap">
						<Chip v-for="tag in tags" :key="tag.id || tag.name" :label="tag" :small="small" />
					</div>
				</v-card-text>
				<v-card-actions class="justify-end py-2 px-3">
					<v-btn variant="text" @click="dialogVisible = false">Close</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>
<script lang="ts">
	import { type Label } from "@/types/Label";
	import Chip from "@/components/tags/DisplayChip.vue";
	import { useDisplay } from "vuetify";

	export default {
		name: "ComboBox",
		components: {
			Chip
		},
		setup() {
			const display = useDisplay();
			return { display };
		},
		props: {
			value: {
				type: Array as () => Label[],
				default: () => []
			},
			modelValue: {
				type: Array as () => Label[],
				default: undefined
			},
			showBadge: {
				type: Boolean,
				default: true
			},
			closable: {
				type: Boolean,
				default: false
			},
			small: {
				type: Boolean,
				default: false
			},
			maxVisible: {
				type: Number,
				default: 3
			},
			bottomTooltip: {
				type: Boolean,
				default: false
			},
			absoluteBadge: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				dialogVisible: false,
				tooltipActive: false,
				tooltipCloseTimer: null as any,
				isTouch: false
			};
		},
		mounted() {
			this.isTouch = matchMedia("(hover: none), (pointer: coarse)").matches;
		},
		computed: {
			tags(): Label[] {
				return this.modelValue || this.value || [];
			},
			visibleTags(): Label[] {
				return this.tags.slice(0, Math.max(0, this.maxVisible));
			},
			remainingCount(): number {
				const total = this.tags.length;
				return total > this.maxVisible ? total - this.maxVisible : 0;
			},
			// The overflow dialog is a touch/narrow-viewport affordance; on a desktop pointer the
			// hover tooltip is the real one. So the chips only accept a click where one does something.
			canOpenDialog(): boolean {
				return this.remainingCount > 0 && (this.display.mobile.value || this.isTouch);
			},
			overflowChipListeners(): Record<string, () => void> {
				return this.canOpenDialog ? { click: this.maybeOpenDialog } : {};
			}
		},
		methods: {
			onActivatorEnter() {
				this.cancelTooltipClose();
				if (this.remainingCount > 0) {
					this.tooltipActive = true;
				}
			},
			onActivatorLeave() {
				this.scheduleTooltipClose();
			},
			onTooltipEnter() {
				this.cancelTooltipClose();
			},
			onTooltipLeave() {
				this.scheduleTooltipClose();
			},
			scheduleTooltipClose(delay: number = 50) {
				this.cancelTooltipClose();
				this.tooltipCloseTimer = window.setTimeout(() => {
					this.tooltipActive = false;
					this.tooltipCloseTimer = null;
				}, delay);
			},
			cancelTooltipClose() {
				if (this.tooltipCloseTimer) {
					clearTimeout(this.tooltipCloseTimer);
					this.tooltipCloseTimer = null;
				}
			},
			maybeOpenDialog() {
				if (this.remainingCount > 0 && (this.display.mobile.value || this.isTouch)) {
					this.dialogVisible = true;
				}
			}
		}
	};
</script>
<style>
	.custom-tooltip {
		background-color: transparent !important;
		padding: 0 !important;
		pointer-events: auto !important;
	}

	.custom-tooltip:before,
	.custom-tooltip:after {
		display: none !important;
	}

	.tag-tooltip-card {
		width: 280px;
		border: 1px solid #e0e0e0;
	}

	.tag-tooltip-content {
		max-height: 140px;
		overflow-y: auto;
	}

	.tag-dialog-content {
		max-height: 240px;
	}

	.badge-anchor {
		display: block;
		width: 1px;
		height: 1px;
	}

	/* WebKit browsers (Chrome, Safari, Edge) */
	.tag-tooltip-content::-webkit-scrollbar {
		-webkit-appearance: none;
		width: 7px;
	}

	.tag-tooltip-content::-webkit-scrollbar-thumb {
		border-radius: 5px;
		background-color: rgba(0, 0, 0, 0.5);
		-webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
	}
</style>
